/**
 * THE INSPECTOR — the fields of whatever is selected, and nothing else.
 *
 * The canvas shows the words; this is where you type them. It is a panel and not a popover
 * on the node because the sentence a caller hears is often two lines long, and a text box
 * floating over the graph covers the two steps either side of it — the context you need to
 * write the sentence.
 *
 * TWO THINGS HERE ARE NOT COSMETIC:
 *
 * 1. THE "do" NODE STATES ITS GATE IN WORDS. Doctrine 2 says every AI action is a proposal a
 *    person accepts or rejects, and the place that has to be unambiguous is the screen where
 *    somebody adds one to their phone line at midnight. It is not a padlock icon and a hover
 *    tooltip; it is a sentence.
 * 2. A BRANCH OUTCOME CANNOT BE BLANK. The outlet's name IS the case value the compiler
 *    reads, so a blank one is a wire the assistant cannot follow. The Add button is inert
 *    until you have typed something and a rename to nothing reverts, rather than letting you
 *    create the broken thing and explaining it afterwards.
 */
import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject, ReactNode, Ref } from 'react';

import { Badge, Callout, Icon, Input, Select, Textarea } from '../index.ts';
import { MESSAGE_CATEGORIES, NODE_SPECS, toolsFor } from './model.ts';
import type { NdField, NdKind } from './model.ts';
import { casesOf } from './graph.ts';
import type { NdNode } from './graph.ts';

export type SituationOption = { key: string; label: string; kind: 'posture' | 'overlay' };

export type InspectorProps = {
  node: NdNode | null;
  readOnly: boolean;
  issues: string[];
  situations: SituationOption[];
  onPatch: (id: string, patch: Record<string, unknown>) => void;
  onAddCase: (id: string, value: string) => void;
  onRenameCase: (id: string, from: string, to: string) => void;
  onRemoveCase: (id: string, value: string) => void;
  onDelete: (id: string) => void;
  /** Bumped by the screen when the operator asks to edit a node from the canvas. */
  focusToken: number;
};

export function Inspector(props: InspectorProps): ReactNode {
  const { node, readOnly, issues, focusToken } = props;
  const firstFieldRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (focusToken > 0) firstFieldRef.current?.focus();
  }, [focusToken, node?.id]);

  if (!node) {
    return (
      <aside className="ndf-inspector" aria-label="Step details">
        <div className="ndf-inspector-empty">
          <Icon name="sliders" size={18} />
          <p className="ndf-inspector-empty-title">Nothing selected</p>
          <p className="ndf-inspector-empty-body">
            Click a step on the canvas — or tab to it and press Enter — to change its words.
            The first Tab on this page is a shortcut straight to the steps.
          </p>
        </div>
      </aside>
    );
  }

  const kind = node.type as NdKind;
  const spec = NODE_SPECS[kind];

  return (
    <aside className="ndf-inspector" aria-label={`${spec.label} details`}>
      <header className="ndf-inspector-head">
        <span className={`ndf-inspector-chip ndf-node--${spec.tone}`} aria-hidden="true">
          <Icon name={spec.icon} size={14} />
        </span>
        <div className="ndf-inspector-title">
          <h3>{spec.label}</h3>
          <p>{spec.blurb}</p>
        </div>
      </header>

      {kind === 'do' ? (
        <Callout tone="ai" icon="sparkle">
          <b>This is proposed, never performed.</b> When the call reaches this step the assistant
          writes a proposal and moves on. Nothing changes for the customer until a person opens
          Proposals and approves it.
        </Callout>
      ) : null}

      {kind === 'page' ? (
        <Callout tone="rule" icon="lock">
          The caller leaves a voicemail first, every time. NightDesk has no step that connects a
          caller to a technician, and there is no setting that adds one.
        </Callout>
      ) : null}

      {kind === 'arm' ? (
        <Callout tone="warn" icon="bolt">
          <b>This outlives the call.</b> Switching a mode on changes what every later caller hears
          until somebody releases it — it is not limited to the person on the phone.
        </Callout>
      ) : null}

      {issues.length > 0 ? (
        <div className="ndf-inspector-issues" role="alert">
          {issues.map((message) => (
            <p key={message}>
              <Icon name="alert-triangle" size={12} /> {message}
            </p>
          ))}
        </div>
      ) : null}

      <div className="ndf-inspector-fields">
        {spec.fields.length === 0 ? (
          <p className="ndf-inspector-note">
            {kind === 'entry'
              ? 'Every call into this flow starts here. There is nothing to set — what starts the flow is the flow’s own trigger, at the top of this screen.'
              : 'Nothing to set. This step hangs up.'}
          </p>
        ) : (
          spec.fields.map((field, index) => (
            <FieldControl
              key={field.key}
              field={field}
              node={node}
              readOnly={readOnly}
              situations={props.situations}
              inputRef={index === 0 ? firstFieldRef : undefined}
              onPatch={props.onPatch}
              onAddCase={props.onAddCase}
              onRenameCase={props.onRenameCase}
              onRemoveCase={props.onRemoveCase}
            />
          ))
        )}
      </div>

      {!readOnly && kind !== 'entry' ? (
        <footer className="ndf-inspector-foot">
          <button type="button" className="ndf-danger-link" onClick={() => props.onDelete(node.id)}>
            <Icon name="minus" size={12} /> Remove this step
          </button>
          <p className="ndf-inspector-note">
            The steps either side of it are joined back together, so nothing after it is stranded.
          </p>
        </footer>
      ) : null}
    </aside>
  );
}

// --------------------------------------------------------------------------------- fields

type FieldControlProps = {
  field: NdField;
  node: NdNode;
  readOnly: boolean;
  situations: SituationOption[];
  inputRef?: MutableRefObject<HTMLElement | null>;
  onPatch: (id: string, patch: Record<string, unknown>) => void;
  onAddCase: (id: string, value: string) => void;
  onRenameCase: (id: string, from: string, to: string) => void;
  onRemoveCase: (id: string, value: string) => void;
};

function FieldControl(props: FieldControlProps): ReactNode {
  const { field, node, readOnly } = props;
  const kind = node.type as NdKind;
  const value = typeof node.data[field.key] === 'string' ? (node.data[field.key] as string) : '';

  if (field.kind === 'cases') {
    return <CasesEditor {...props} />;
  }

  if (field.kind === 'longtext') {
    return (
      <Textarea
        ref={props.inputRef as unknown as Ref<HTMLTextAreaElement>}
        label={field.label}
        hint={field.hint}
        rows={3}
        value={value}
        placeholder={field.placeholder}
        disabled={readOnly}
        onChange={(event) => props.onPatch(node.id, { [field.key]: event.target.value })}
      />
    );
  }

  if (field.kind === 'tool') {
    const tools = toolsFor(kind);
    const chosen = tools.find((t) => t.name === value);
    return (
      <Select
        ref={props.inputRef as unknown as Ref<HTMLSelectElement>}
        label={field.label}
        hint={chosen ? chosen.blurb : 'Only the things this assistant is actually wired to do are offered.'}
        value={value}
        placeholder="Choose one…"
        disabled={readOnly}
        options={tools.map((t) => ({ value: t.name, label: t.label }))}
        onChange={(event) => props.onPatch(node.id, { [field.key]: event.target.value })}
      />
    );
  }

  if (field.kind === 'situation') {
    const chosen = props.situations.find((s) => s.key === value);
    return (
      <Select
        ref={props.inputRef as unknown as Ref<HTMLSelectElement>}
        label={field.label}
        hint={
          chosen
            ? chosen.kind === 'posture'
              ? 'A posture: it replaces whatever the line is doing now.'
              : 'An overlay: it is added on top of whatever the line is doing now.'
            : 'These are the modes on your mode bar. Switching one on here does the same thing as pressing its button.'
        }
        value={value}
        placeholder="Choose a mode…"
        disabled={readOnly}
        options={props.situations.map((s) => ({ value: s.key, label: s.label }))}
        onChange={(event) => props.onPatch(node.id, { [field.key]: event.target.value })}
      />
    );
  }

  if (field.kind === 'category') {
    return (
      <Select
        ref={props.inputRef as unknown as Ref<HTMLSelectElement>}
        label={field.label}
        hint="What the morning inbox groups it under."
        value={value || 'customer'}
        disabled={readOnly}
        options={MESSAGE_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
        onChange={(event) => props.onPatch(node.id, { [field.key]: event.target.value })}
      />
    );
  }

  return (
    <Input
      ref={props.inputRef as unknown as Ref<HTMLInputElement>}
      label={field.label}
      hint={field.hint}
      value={value}
      mono={field.key === 'key' || field.key === 'on'}
      placeholder={field.placeholder}
      disabled={readOnly}
      onChange={(event) => props.onPatch(node.id, { [field.key]: event.target.value })}
    />
  );
}

/**
 * The outcomes of a branch. Each row is one outlet on the card, and the row's text IS the
 * case the compiler reads — so a rename moves the wire with it (see renameCase in graph.ts)
 * and there is no path through this control that produces a blank one.
 */
function CasesEditor(props: FieldControlProps): ReactNode {
  const { node, readOnly } = props;
  const cases = casesOf(node);
  const [draft, setDraft] = useState('');
  const clash = cases.includes(draft.trim());

  return (
    <div className="ndf-cases-editor">
      <span className="nd-label">Outcomes</span>
      <p className="nd-hint">
        One outlet per outcome. The assistant compares the answer to these words, so use the ones a
        caller would actually say.
      </p>

      <ul className="ndf-cases-rows">
        {cases.map((value) => (
          <li key={value}>
            <CaseRow
              value={value}
              readOnly={readOnly}
              onRename={(next) => props.onRenameCase(node.id, value, next)}
              onRemove={() => props.onRemoveCase(node.id, value)}
              canRemove={cases.length > 1}
            />
          </li>
        ))}
        {cases.length === 0 ? (
          <li className="ndf-cases-none">No outcomes yet — this branch has nowhere to send a call.</li>
        ) : null}
      </ul>

      {readOnly ? null : (
        <div className="ndf-cases-add">
          <Input
            label="Add an outcome"
            value={draft}
            mono
            placeholder="billing"
            error={clash ? 'That outcome already exists.' : undefined}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              if (!draft.trim() || clash) return;
              props.onAddCase(node.id, draft.trim());
              setDraft('');
            }}
          />
          <button
            type="button"
            className="ndf-cases-addbtn"
            disabled={!draft.trim() || clash}
            onClick={() => {
              props.onAddCase(node.id, draft.trim());
              setDraft('');
            }}
          >
            <Icon name="plus" size={12} /> Add
          </button>
        </div>
      )}
    </div>
  );
}

function CaseRow({
  value,
  readOnly,
  canRemove,
  onRename,
  onRemove,
}: {
  value: string;
  readOnly: boolean;
  canRemove: boolean;
  onRename: (next: string) => void;
  onRemove: () => void;
}): ReactNode {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  /**
   * Reads the value off the element rather than off `draft`. React batches state, so a blur
   * that lands in the same tick as the last keystroke sees a stale `draft` and silently
   * throws the rename away — which is exactly the sort of bug that only shows up when
   * somebody types fast and then clicks straight onto the canvas.
   */
  const commit = (raw: string): void => {
    const next = raw.trim();
    // A blank outcome is a wire the assistant cannot follow, so it is not a state this
    // control can leave you in — it snaps back rather than saving nothing.
    if (!next) {
      setDraft(value);
      return;
    }
    if (next !== value) onRename(next);
  };

  return (
    <div className="ndf-case-row">
      <input
        className="ndf-case-input nd-mono"
        value={draft}
        disabled={readOnly}
        aria-label={`Outcome: ${value}`}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit((event.target as HTMLInputElement).value);
          }
          if (event.key === 'Escape') setDraft(value);
        }}
      />
      {readOnly ? null : canRemove ? (
        <button type="button" className="ndf-case-remove" onClick={onRemove} title={`Remove the “${value}” outcome`}>
          <Icon name="close" size={12} />
          <span className="nd-sr">{`Remove the ${value} outcome`}</span>
        </button>
      ) : (
        <Badge tone="plain" size="sm" title="A branch needs at least one outcome.">
          kept
        </Badge>
      )}
    </div>
  );
}

export default Inspector;
